import { PropsWithChildren, useEffect } from 'react';
import { Redirect, Route, RouteProps, useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Layout, Modal, message } from 'antd';

import ApplicationHeader from '@/components/PrivateLayout/ApplicationHeader';
import ApplicationFooter from '@/components/PrivateLayout/ApplicationFooter';

import './style.less';
import { RouteType } from '@/types/RouteType';
import { getUserFromLocalStorage } from '@/utils/user';
import jwtDecode from 'jwt-decode';
import moment from 'moment';
import { authOperations } from '@/state/ducks/auth';
import { useDispatch } from 'react-redux';
import { useQueryClient } from 'react-query';
import { store } from '@/Root';
import { useWindowSize } from '@/hooks';
import { useIntl } from 'react-intl';
import Vision from '@/components/Vision';
import InvalidAfterNotification from '@/components/_common/InvalidAfterNotification';
import RequestQueueStatus from '@/components/RequestQueueStatus';
import { APP_NAME } from '@/index';
import UserGuiding from '@/core/UserGuinding';
import ChatAI from '@/core/libs/ChatAI';

const { Content } = Layout;

const PrivateLayout = ({
  path,
  exact,
  children,
  routes,
}: PropsWithChildren<RouteProps & RouteType>) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const userData = getUserFromLocalStorage();
  const queryClient = useQueryClient();
  const { isMobile } = useWindowSize();
  const intl = useIntl();

  useEffect(() => {
    if (userData.accessToken) {
      const tokenData = jwtDecode<{ exp?: number }>(userData.accessToken);

      if (tokenData && tokenData.exp) {
        const timestamp = tokenData.exp * 1000;

        if (
          !moment(timestamp).isValid() ||
          moment(moment(timestamp).subtract(30, 'minutes')).isSameOrBefore(
            moment(),
          )
        ) {
          refreshToken();
        } else {
          UserGuiding.identifyUser(userData?.ugHash || undefined);
        }
      }
    }
  }, [userData]);

  useEffect(() => {
    if (isMobile) {
      message.open({
        content: intl.formatMessage({ id: 'commons.message.info.mobileTable' }),
      });
    }
  }, [isMobile]);

  async function refreshToken() {
    try {
      await store.dispatch(authOperations.handleRefreshToken());
    } catch (error) {
      Modal.confirm({
        title: 'Sessão expirada',
        content:
          'Sua sessão foi expirada devido ao tempo sem atividades. Por favor, faça login novamente.',
        cancelButtonProps: { style: { display: 'none' } },
        okText: 'Fechar',
      });
      logout();
    }
  }

  function logout() {
    queryClient.clear();
    dispatch(authOperations.handleLogout());
    history.replace(`/login?redirect=${window.location.pathname}`);
  }

  if (!userData.accessToken || !userData.refreshToken)
    return <Redirect to={`/login?redirect=${window.location.pathname}`} />;

  return (
    <>
      <Route
        path={path}
        exact={exact}
        render={() => (
          <Layout style={{ minHeight: '100vh' }} className="layout-fill">
            <Helmet
              titleTemplate={`%s | ${APP_NAME}`}
              defaultTitle={APP_NAME}
            />
            <ApplicationHeader routes={routes} />
            <Content className="content-wrapper">
              <div className="content-wrapper-content">{children}</div>
              <RequestQueueStatus />
              <Vision />
            </Content>
            <ApplicationFooter />
            <InvalidAfterNotification />
          </Layout>
        )}
      />

      <ChatAI />
    </>
  );
};

export default PrivateLayout;
